/** Elements whose text is never translated (code, styles, scripts, editors). */
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TITLE", "CODE", "TEXTAREA", "SVG"]);
/** Digits, punctuation and symbols only — nothing to translate. */
const NOT_TRANSLATABLE = /^[\s\d\p{P}\p{S}]*$/u;
/** A bare link or address. */
const URL_OR_EMAIL = /^(https?:\/\/|www\.)\S+$|^\S+@\S+\.\S+$/i;

export interface TranslatedHtml {
    html: string;
    /** True when only the first part of a long message was translated. */
    partial: boolean;
}

/**
 * Translates an email in place, the way Outlook and Chrome do: every visible
 * text node is translated and written back where it was, so images, tables,
 * colours and links keep their exact layout — instead of the message being
 * replaced by a plain-text block.
 *
 * The HTML is parsed with DOMParser, whose documents are inert: no script
 * runs and no image is fetched while we walk it.
 */
export async function translateEmailHtml(
    html: string,
    translate: (segments: string[]) => Promise<{ segments: string[]; partial: boolean }>
): Promise<TranslatedHtml> {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const nodes: Text[] = [];
    const segments: string[] = [];

    const walker = doc.createTreeWalker(doc.documentElement, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        let skipped = false;
        for (let el = node.parentElement; el; el = el.parentElement) {
            if (SKIP_TAGS.has(el.tagName.toUpperCase())) { skipped = true; break; }
        }
        if (skipped) continue;
        const text = (node.nodeValue ?? "").replace(/\s+/g, " ").trim();
        if (!text || NOT_TRANSLATABLE.test(text) || URL_OR_EMAIL.test(text)) continue;
        nodes.push(node as Text);
        segments.push(text);
    }
    if (segments.length === 0) throw new Error("Nothing to translate.");

    const result = await translate(segments);
    nodes.forEach((node, i) => {
        const translated = result.segments[i];
        if (translated === undefined) return;
        // Keep the node's own surrounding whitespace so inline spacing
        // ("Hello <b>name</b>,") survives the swap.
        const raw = node.nodeValue ?? "";
        const lead = /^\s*/.exec(raw)?.[0] ?? "";
        const trail = /\s*$/.exec(raw)?.[0] ?? "";
        node.nodeValue = lead + translated + trail;
    });

    return { html: doc.documentElement.outerHTML, partial: result.partial };
}
